import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  containerSelector,
  containersSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  optionsSelector,
  filterFieldSelector,
  containerCodeSelector,
  containerProductsSelector,
} from './store/selector';
import { Container } from '../../interfaces/container.interface';
import { PutContainerPayloadRequest } from './interfaces/put-container';
import { containersApiService } from './services/api';
import {
  getContainersInitApiAction,
  getContainersSuccessApiAction,
  getContainersFailureApiAction,
  postContainerInitApiAction,
  postContainerFailuireApiAction,
  putContainerInitApiAction,
  putContainerFailuireApiAction,
  deleteContainerInitApiAction,
  deleteContainerFailuireApiAction,
  setContainerAction,
  increasePageAction,
  containerClearAction,
  postContainerSuccessApiAction,
  deleteContainerSuccessApiAction,
  putContainerSuccessApiAction,
  resetOptionsAction,
  setOptionsAction,
  setFilterFieldAction,
  patchContainerLocationInitApiAction,
  patchContainerLocationSuccessApiAction,
  patchContainerLocationFailuireApiAction,
  setContainerCodeAction,
  getContainerProductsInitApiAction,
  getContainerProductsSuccessApiAction,
  getContainerProductsFailureApiAction,
  postProductToContainerInitApiAction,
  postProductToContainerFailuireApiAction,
  postProductToContainerSuccessApiAction,
} from './store/reducer';
import { PostContainerPayloadRequest } from './interfaces/post-container';
import { GetContainersParams } from './interfaces/get-containers';
import { PostLightContainerPayloadRequest } from './interfaces/post-light-container';
import { PostProductToContainerPayloadRequest } from './interfaces/post-product-to-container';

export function useContainerCore() {
  const containers = useSelector(containersSelector);
  const products = useSelector(containerProductsSelector);
  const container = useSelector(containerSelector);
  const total = useSelector(totalSelector);
  const options = useSelector(optionsSelector);
  const filterField = useSelector(filterFieldSelector);
  const containerCode = useSelector(containerCodeSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getContainers = useCallback(
    async (
      id: number,
      params: GetContainersParams,
      onSuccess?: (containers: Container[]) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getContainersInitApiAction());
        const response = await containersApiService.getContainers(id, params);
        dispatch(getContainersSuccessApiAction(response));
        onSuccess?.(response.containers);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getContainersFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getContainerProducts = useCallback(
    async (
      cId: number,
      tId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getContainerProductsInitApiAction());
        const response = await containersApiService.getContainerProducts(cId, tId);
        dispatch(getContainerProductsSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getContainerProductsFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postContainer = useCallback(
    async (
      id: number,
      body: PostContainerPayloadRequest,
      onSuccess?: (id: number, code: string) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postContainerInitApiAction());
        const response = await containersApiService.postContainer(id, body);
        dispatch(postContainerSuccessApiAction());
        onSuccess?.(response.id, response.code);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postLightContainer = useCallback(
    async (
      id: number,
      body: PostLightContainerPayloadRequest,
      onSuccess?: (id: number, code: string) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postContainerInitApiAction());
        const response = await containersApiService.postLightContainer(id, body);
        dispatch(postContainerSuccessApiAction());
        onSuccess?.(response.id, response.code);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postProductToContainer = useCallback(
    async (
      cId: number,
      tId: number,
      body: PostProductToContainerPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postProductToContainerInitApiAction());
        await containersApiService.postProductToContainer(cId, tId, body);
        dispatch(postProductToContainerSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postProductToContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postProductToLocation = useCallback(
    async (
      cId: number,
      tId: number,
      body: PostProductToContainerPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postProductToContainerInitApiAction());
        await containersApiService.postProductToLocation(cId, tId, body);
        dispatch(postProductToContainerSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postProductToContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putContainer = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutContainerPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putContainerInitApiAction());
        await containersApiService.putContainer(cId, pId, body);
        dispatch(putContainerSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putContainerFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const patchContainerLocation = useCallback(
    async (
      cId: number,
      pId: number,
      lId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(patchContainerLocationInitApiAction());
        await containersApiService.patchContainerLocation(cId, pId, lId);
        dispatch(patchContainerLocationSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(patchContainerLocationFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteContainer = useCallback(
    async (
      cId: number,
      pId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(deleteContainerInitApiAction());
        await containersApiService.deleteContainer(cId, pId);
        dispatch(deleteContainerSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteContainerFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setContainer = useCallback(
    (container: Container | null) => {
      dispatch(setContainerAction(container));
    },
    [dispatch]
  );

  const setOptions = useCallback(
    (options: GetContainersParams) => {
      dispatch(setOptionsAction(options));
    },
    [dispatch]
  );

  const setContainerCode = useCallback((code: string) => {
    dispatch(setContainerCodeAction(code));
  }, [dispatch]);

  const setFilterField = useCallback((field: string) => {
    dispatch(setFilterFieldAction(field));
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearContainer = useCallback(() => {
    dispatch(containerClearAction());
  }, [dispatch]);

  const postContainerFile = useCallback(
    async (
      cId: number,
      containerId: number,
      file: File,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        await containersApiService.postContainerFile(cId, containerId, file);
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
      }
    },
    []
  );

  const deleteContainerFile = useCallback(
    async (
      cId: number,
      containerId: number,
      fileId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        await containersApiService.deleteContainerFile(cId, containerId, fileId);
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
      }
    },
    []
  );

  return {
    containers,
    products,
    container,
    total,
    page,
    options,
    containerCode,
    filterField,
    noMore,
    errorCode,
    getContainers,
    postContainer,
    postLightContainer,
    putContainer,
    deleteContainer,
    patchContainerLocation,
    setContainerCode,
    getContainerProducts,
    postProductToContainer,
    postProductToLocation,
    postContainerFile,
    deleteContainerFile,
    setContainer,
    increasePage,
    clearContainer,
    setOptions,
    setFilterField,
    resetOptions,
  };
}
